/**
 * Who may come, as a fact the calendar can filter on (D-322).
 *
 * `events.age_restriction` was the only record of this, as free text, so "21+",
 * "21 and over" and "21+ with ID" were three different answers to one question
 * and nothing could ask "which shows can I bring my kid to". `minimum_age` is
 * the question's answer as a number: 0 is all ages, 21 is twenty-one and over.
 *
 * The free text survives as a *note* for what a number cannot say ("valid photo
 * ID required"). What a customer reads is built here from both, once, so the
 * event page, the wallet pass and a printed ticket cannot disagree about it.
 *
 * Zero is the default because it is what the storefront already said: an event
 * with nothing set has always printed "All ages welcome."
 */

/** The ages Admin offers. A stored value outside these still shows and round-trips. */
export const MINIMUM_AGE_CHOICES = [0, 13, 16, 18, 21] as const;

/** "All ages", or "21+". */
export function ageLabel(minimumAge: number): string {
  return minimumAge <= 0 ? 'All ages' : `${minimumAge}+`;
}

/**
 * The age line a customer reads, or null when there is nothing to say.
 *
 * All ages with no note prints nothing, because the surfaces that show this
 * already say "All ages welcome" in their own words when it is empty. A note
 * that already names the age, however it puts it ("21+ with valid ID", "13 and
 * over unless accompanied"), is printed as written rather than after a label
 * that says the same thing.
 */
export function ageLine(minimumAge: number, note: string | null | undefined): string | null {
  const text = note?.trim() || null;
  if (minimumAge <= 0) return text;
  const label = ageLabel(minimumAge);
  if (!text) return label;
  return new RegExp(`(^|\\D)${minimumAge}(\\D|$)`).test(text) ? text : `${label} · ${text}`;
}
